import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, Save, X, Copy, Upload as UploadIcon, Download, FileDown, Undo2, Check, Minus, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { toast as toastify } from 'react-toastify';
import R2Upload from '../R2Upload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideosTable = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddVideoForm, setShowAddVideoForm] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [importing, setImporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // { toCreate: [...], toUpdate: [...], skipped: [...] }
  const [lastImport, setLastImport] = useState(null); // { created: [id], updated: [{id, originalPayload}], timestamp, stats }
  const [undoing, setUndoing] = useState(false);
  const fileInputRef = React.useRef(null);
  const exportMenuRef = React.useRef(null);
  const { toast } = useToast();

  const [videoFormData, setVideoFormData] = useState({
    title: '',
    description: '',
    thumbnail: '',
    videoPreview: '',
    duration: '',
    videoCode: '',
    tags: ''
  });

  const UNDO_WINDOW_MS = 60 * 60 * 1000; // 1 hour

  const clearLastImport = () => {
    setLastImport(null);
    try { localStorage.removeItem('leolulu_last_import'); } catch (e) { console.warn("localStorage access failed:", e); }
  };

  useEffect(() => {
    fetchVideos();
    // Load last import snapshot from localStorage
    try {
      const raw = localStorage.getItem('leolulu_last_import');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && (parsed.created?.length || parsed.updated?.length)) {
          // Check 1h expiry
          const age = Date.now() - (parsed.timestamp || 0);
          if (age > UNDO_WINDOW_MS) {
            localStorage.removeItem('leolulu_last_import');
          } else {
            setLastImport(parsed);
          }
        }
      }
    } catch (e) { console.warn("localStorage access failed:", e); }
  }, []);

  // Validate lastImport against current videos + expiry; hide button if no referenced cards remain
  useEffect(() => {
    if (!lastImport) return;
    const age = Date.now() - (lastImport.timestamp || 0);
    if (age > UNDO_WINDOW_MS) {
      clearLastImport();
      return;
    }
    const ids = new Set(videos.map(v => v.id));
    const stillCreated = (lastImport.created || []).some(id => ids.has(id));
    const stillUpdated = (lastImport.updated || []).some(item => ids.has(item.id));
    if (!stillCreated && !stillUpdated) {
      clearLastImport();
    }
  }, [videos, lastImport]);

  // Auto-expire timer: hide the button once the 1h window passes while the tab is open
  useEffect(() => {
    if (!lastImport) return;
    const age = Date.now() - (lastImport.timestamp || 0);
    const remaining = UNDO_WINDOW_MS - age;
    if (remaining <= 0) {
      clearLastImport();
      return;
    }
    const t = setTimeout(() => clearLastImport(), remaining);
    return () => clearTimeout(t);
  }, [lastImport]);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`, { withCredentials: true });
      setVideos(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast({
        title: "Error",
        description: "Failed to load videos",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleVideoInputChange = (e) => {
    const { name, value } = e.target;
    setVideoFormData(prev => ({ ...prev, [name]: value }));
  };

  // ========== IMPORT / EXPORT HELPERS ==========
  const COLUMN_MAP = {
    'id': 'id',
    'title': 'title',
    'thumbnail': 'thumbnail',
    'video preview': 'videoPreview',
    'videopreview': 'videoPreview',
    'description': 'description',
    'duration': 'duration',
    'video code': 'videoCode',
    'videocode': 'videoCode',
    'tags': 'tags'
  };

  const normalizeRow = (row) => {
    const out = {};
    Object.keys(row).forEach(key => {
      const norm = String(key).trim().toLowerCase();
      const target = COLUMN_MAP[norm];
      if (target) {
        const val = row[key];
        out[target] = val === undefined || val === null ? '' : String(val).trim();
      }
    });
    // Auto-prepend # to Video Code if missing
    if (out.videoCode && !out.videoCode.startsWith('#')) {
      out.videoCode = '#' + out.videoCode;
    }
    return out;
  };

  const handleImportClick = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!window.XLSX) {
      toastify.error('SheetJS library not loaded. Please reload the page.');
      return;
    }

    setImporting(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = window.XLSX.read(arrayBuffer, { type: 'array', cellDates: false });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = window.XLSX.utils.sheet_to_json(firstSheet, { defval: '', raw: false });

      // release file from memory
      if (fileInputRef.current) fileInputRef.current.value = '';

      if (!rows || rows.length === 0) {
        toastify.error('Invalid file format. Please use the template.');
        setImporting(false);
        return;
      }

      // Validate at least one recognized column exists
      const firstRowKeys = Object.keys(rows[0]).map(k => String(k).trim().toLowerCase());
      const hasValidColumn = firstRowKeys.some(k => COLUMN_MAP[k]);
      if (!hasValidColumn) {
        toastify.error('Invalid file format. Please use the template.');
        setImporting(false);
        return;
      }

      const existingById = new Map(videos.map(v => [v.id, v]));
      const toCreate = [];
      const toUpdate = [];
      const skipped = [];

      rows.forEach((raw, idx) => {
        const row = normalizeRow(raw);
        const hasAnyValue = Object.values(row).some(v => v !== '');
        if (!hasAnyValue) return;

        if (row.id && existingById.has(row.id)) {
          const existing = existingById.get(row.id);
          const payload = {
            title: row.title || existing.title,
            description: row.description || existing.description,
            thumbnail: row.thumbnail || existing.thumbnail,
            videoPreview: row.videoPreview !== '' ? row.videoPreview : (existing.videoPreview || ''),
            duration: row.duration || existing.duration,
            videoCode: row.videoCode || existing.videoCode,
            tags: row.tags !== ''
              ? row.tags.split(',').map(t => t.trim()).filter(Boolean)
              : existing.tags
          };
            toUpdate.push({
              id: row.id,
              existingTitle: existing.title,
              payload,
              originalPayload: {
                title: existing.title || '',
                description: existing.description || '',
                thumbnail: existing.thumbnail || '',
                videoPreview: existing.videoPreview || '',
                duration: existing.duration || '',
                videoCode: existing.videoCode || '',
                tags: Array.isArray(existing.tags) ? [...existing.tags] : []
              }
            });
        } else {
          const payload = {
            title: row.title || '',
            description: row.description || '',
            thumbnail: row.thumbnail || '',
            videoPreview: row.videoPreview || '',
            duration: row.duration || '',
            videoCode: row.videoCode || '',
            tags: row.tags
              ? row.tags.split(',').map(t => t.trim()).filter(Boolean)
              : []
          };
          const missing = [];
          if (!payload.title) missing.push('Title');

          if (missing.length > 0) {
            skipped.push({ row: idx + 2, reason: `Missing: ${missing.join(', ')}`, payload });
          } else {
            toCreate.push({ payload });
          }
        }
      });

      if (toCreate.length === 0 && toUpdate.length === 0) {
        toastify.warn('Nothing to import. Check the file contents.');
        setImporting(false);
        return;
      }

      setImportPreview({ toCreate, toUpdate, skipped });
    } catch (error) {
      console.error('Import parse error:', error);
      toastify.error('Failed to parse file. Please use the template.');
    } finally {
      setImporting(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!importPreview) return;
    const { toCreate, toUpdate } = importPreview;
    setImporting(true);

    let created = 0;
    let updated = 0;
    let failed = 0;
    const createdIds = [];
    const updatedOriginals = [];

    for (const item of toUpdate) {
      try {
        await axios.put(`${API}/videos/${item.id}`, item.payload, { withCredentials: true });
        updatedOriginals.push({ id: item.id, originalPayload: item.originalPayload });
        updated++;
      } catch (e) {
        console.error('Update error:', e);
        failed++;
      }
    }
    for (const item of toCreate) {
      try {
        const resp = await axios.post(`${API}/videos`, item.payload, { withCredentials: true });
        const newId = resp.data?.id || resp.data?._id;
        if (newId) createdIds.push(newId);
        created++;
      } catch (e) {
        console.error('Create error:', e);
        failed++;
      }
    }

    // Refresh list first, THEN save snapshot (so the existence-check effect sees new IDs)
    await fetchVideos();

    // Save undo snapshot (created + updated originals)
    if (createdIds.length > 0 || updatedOriginals.length > 0) {
      const snapshot = {
        created: createdIds,
        updated: updatedOriginals,
        timestamp: Date.now(),
        stats: { created, updated }
      };
      setLastImport(snapshot);
      try {
        localStorage.setItem('leolulu_last_import', JSON.stringify(snapshot));
      } catch (e) { console.warn("localStorage access failed:", e); }
    }

    const msg = `${created} videos imported, ${updated} updated${failed ? `, ${failed} failed` : ''}`;
    toastify.success(msg, { position: 'top-right', autoClose: 4000 });
    setImportPreview(null);
    setImporting(false);
  };

  const handleCancelImport = () => {
    setImportPreview(null);
  };

  const handleUndoImport = async () => {
    if (!lastImport) return;
    const { created, updated, stats } = lastImport;
    const parts = [];
    if (created.length) parts.push(`${created.length} created`);
    if (updated.length) parts.push(`${updated.length} updated`);
    const confirmMsg = `Undo last import? This will revert: ${parts.join(', ')}.`;
    if (!window.confirm(confirmMsg)) return;

    setUndoing(true);
    let reverted = 0;
    let errors = 0;

    // Delete created videos
    for (const id of created) {
      try {
        await axios.delete(`${API}/videos/${id}`, { withCredentials: true });
        reverted++;
      } catch (e) {
        console.error('Undo delete error:', e);
        errors++;
      }
    }
    // Restore updated videos to their original values
    for (const item of updated) {
      try {
        await axios.put(`${API}/videos/${item.id}`, item.originalPayload, { withCredentials: true });
        reverted++;
      } catch (e) {
        console.error('Undo restore error:', e);
        errors++;
      }
    }

    if (errors > 0) {
      toastify.warn(`Undo finished with ${reverted} reverted and ${errors} errors.`);
    } else {
      toastify.success(`Undo complete. Reverted ${stats.created} created and ${stats.updated} updated.`);
    }

    setLastImport(null);
    try {
      localStorage.removeItem('leolulu_last_import');
    } catch (e) { console.warn("localStorage access failed:", e); }
    setUndoing(false);
    fetchVideos();
  };

  const buildExportRows = () => {
    // Neutralize spreadsheet formula injection by prefixing risky leading chars
    const safe = (val) => {
      const s = String(val ?? '');
      return /^[=+\-@\t\r]/.test(s) ? "'" + s : s;
    };
    return videos.map(v => ({
      ID: safe(v.id || ''),
      Title: safe(v.title || ''),
      Thumbnail: safe(v.thumbnail || ''),
      'Video Preview': safe(v.videoPreview || ''),
      Description: safe(v.description || ''),
      Duration: safe(v.duration || ''),
      'Video Code': safe(v.videoCode || ''),
      Tags: safe(Array.isArray(v.tags) ? v.tags.join(', ') : (v.tags || ''))
    }));
  };

  const triggerDownload = (blob, filename) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleExportCSV = () => {
    setShowExportMenu(false);
    if (!videos || videos.length === 0) {
      toastify.warn('No videos to export');
      return;
    }
    const rows = buildExportRows();
    const headers = Object.keys(rows[0]);
    const escape = (val) => {
      const s = String(val ?? '');
      if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    triggerDownload(blob, 'videos-export.csv');
  };

  const handleExportXLSX = () => {
    setShowExportMenu(false);
    if (!videos || videos.length === 0) {
      toastify.warn('No videos to export');
      return;
    }
    if (!window.XLSX) {
      toastify.error('SheetJS library not loaded. Please reload the page.');
      return;
    }
    const rows = buildExportRows();
    const worksheet = window.XLSX.utils.json_to_sheet(rows);
    const workbook = window.XLSX.utils.book_new();
    window.XLSX.utils.book_append_sheet(workbook, worksheet, 'Videos');
    const wbout = window.XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    triggerDownload(blob, 'videos-export.xlsx');
  };

  // Close export dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showExportMenu]);


  const handleAddVideo = async () => {
    try {
      const videoData = {
        ...videoFormData,
        tags: videoFormData.tags.split(',').map(tag => tag.trim()).filter(t => t)
      };

      await axios.post(`${API}/videos`, videoData, { withCredentials: true });
      
      toastify.success('Video added successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
      
      setVideoFormData({
        title: '',
        description: '',
        thumbnail: '',
        videoPreview: '',
        duration: '',
        videoCode: '',
        tags: ''
      });
      setShowAddVideoForm(false);
      fetchVideos();
    } catch (error) {
      console.error('Error adding video:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to add video",
        variant: "destructive"
      });
    }
  };

  const handleEditVideo = (video) => {
    setEditingId(video.id);
    setVideoFormData({
      title: video.title,
      description: video.description,
      thumbnail: video.thumbnail,
      videoPreview: video.videoPreview || '',
      duration: video.duration,
      videoCode: video.videoCode,
      tags: video.tags.join(', ')
    });
  };

  const handleUpdateVideo = async (videoId) => {
    try {
      const videoData = {
        ...videoFormData,
        tags: videoFormData.tags.split(',').map(tag => tag.trim()).filter(t => t)
      };

      await axios.put(`${API}/videos/${videoId}`, videoData, { withCredentials: true });
      
      toastify.success('Video updated successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
      
      setEditingId(null);
      setVideoFormData({
        title: '',
        description: '',
        thumbnail: '',
        videoPreview: '',
        duration: '',
        videoCode: '',
        tags: ''
      });
      fetchVideos();
    } catch (error) {
      console.error('Error updating video:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update video",
        variant: "destructive"
      });
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      await axios.delete(`${API}/videos/${videoId}`, { withCredentials: true });
      
      toastify.success('Video deleted successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
      
      fetchVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      toastify.error('Failed to delete video', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedVideos.length === 0) {
      toast({ title: "No videos selected", variant: "destructive" });
      return;
    }
    
    if (!window.confirm(`Delete ${selectedVideos.length} videos?`)) return;
    
    try {
      await axios.post(`${API}/videos/bulk-delete`, selectedVideos, { withCredentials: true });
      toast({ title: "Success", description: `Deleted ${selectedVideos.length} videos` });
      setSelectedVideos([]);
      fetchVideos();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete videos", variant: "destructive" });
    }
  };

  const handleSelectVideo = (videoId) => {
    setSelectedVideos(prev => 
      prev.includes(videoId) ? prev.filter(id => id !== videoId) : [...prev, videoId]
    );
  };

  const handleSelectAll = () => {
    if (selectedVideos.length === videos.length) {
      setSelectedVideos([]);
    } else {
      setSelectedVideos(videos.map(v => v.id));
    }
  };

  const handleCopyId = (videoId) => {
    navigator.clipboard.writeText(videoId);
    toast({
      title: "Copied!",
      description: "Video ID copied to clipboard",
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedVideos = useMemo(() => {
    const sorted = [...videos].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle date fields
      if (sortField === 'createdAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      // Handle numeric fields
      if (sortField === 'price') {
        aVal = parseFloat(aVal.replace('$', '')) || 0;
        bVal = parseFloat(bVal.replace('$', '')) || 0;
      }

      // Handle string fields
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    return sorted;
  }, [videos, sortField, sortDirection]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="inline w-3 h-3 ml-1 text-gray-400" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="inline w-3 h-3 ml-1 text-gray-700" />
      : <ArrowDown className="inline w-3 h-3 ml-1 text-gray-700" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Import Preview Modal */}
      {importPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-testid="import-preview-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-xl font-bold">Confirm Import</h3>
              <button
                onClick={handleCancelImport}
                className="text-gray-400 hover:text-gray-600"
                data-testid="import-preview-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1">
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-green-50 border border-green-200 rounded-md p-3 text-center">
                  <div className="text-2xl font-bold text-green-700" data-testid="preview-create-count">
                    {importPreview.toCreate.length}
                  </div>
                  <div className="text-sm text-green-700">Will be created</div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700" data-testid="preview-update-count">
                    {importPreview.toUpdate.length}
                  </div>
                  <div className="text-sm text-blue-700">Will be updated</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700" data-testid="preview-skipped-count">
                    {importPreview.skipped.length}
                  </div>
                  <div className="text-sm text-amber-700">Skipped</div>
                </div>
              </div>

              {importPreview.toUpdate.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Updates ({importPreview.toUpdate.length})</h4>
                  <div className="border border-gray-200 rounded overflow-auto max-h-96">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-2 whitespace-nowrap">Title</th>
                          <th className="text-left px-2 py-2 whitespace-nowrap">Video Code</th>
                          <th className="text-left px-2 py-2 whitespace-nowrap">Duration</th>
                          <th className="text-left px-2 py-2">Description</th>
                          <th className="text-left px-2 py-2">Thumbnail</th>
                          <th className="text-left px-2 py-2">Video Preview</th>
                          <th className="text-left px-2 py-2">Tags</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.toUpdate.slice(0, 50).map((item) => (
                          <tr key={`upd-${item.id}`} className="border-t align-top">
                            <td className="px-2 py-2">
                              {item.payload.title !== item.originalPayload.title ? (
                                <div>
                                  <div className="text-gray-400 line-through">{item.originalPayload.title}</div>
                                  <div className="font-medium">{item.payload.title}</div>
                                </div>
                              ) : (
                                item.payload.title
                              )}
                            </td>
                            <td className="px-2 py-2 font-mono">{item.payload.videoCode}</td>
                            <td className="px-2 py-2 whitespace-nowrap">{item.payload.duration}</td>
                            <td className="px-2 py-2 max-w-[200px] truncate" title={item.payload.description}>{item.payload.description}</td>
                            <td className="px-2 py-2 max-w-[120px] truncate" title={item.payload.thumbnail}>{item.payload.thumbnail}</td>
                            <td className="px-2 py-2 max-w-[120px] truncate" title={item.payload.videoPreview}>{item.payload.videoPreview}</td>
                            <td className="px-2 py-2">{(item.payload.tags || []).join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {importPreview.toUpdate.length > 50 && (
                      <div className="text-xs text-gray-500 px-3 py-2 bg-gray-50 border-t">
                        ... and {importPreview.toUpdate.length - 50} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              {importPreview.toCreate.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">New Videos ({importPreview.toCreate.length})</h4>
                  <div className="border border-gray-200 rounded overflow-auto max-h-96">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-2 whitespace-nowrap">Title</th>
                          <th className="text-left px-2 py-2 whitespace-nowrap">Video Code</th>
                          <th className="text-left px-2 py-2 whitespace-nowrap">Duration</th>
                          <th className="text-left px-2 py-2">Description</th>
                          <th className="text-left px-2 py-2">Thumbnail</th>
                          <th className="text-left px-2 py-2">Video Preview</th>
                          <th className="text-left px-2 py-2">Tags</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.toCreate.slice(0, 50).map((item) => (
                          <tr key={`new-${item.payload.videoCode || ''}-${item.payload.title}`} className="border-t align-top">
                            <td className="px-2 py-2">{item.payload.title}</td>
                            <td className="px-2 py-2 font-mono">{item.payload.videoCode}</td>
                            <td className="px-2 py-2 whitespace-nowrap">{item.payload.duration}</td>
                            <td className="px-2 py-2 max-w-[200px] truncate" title={item.payload.description}>{item.payload.description}</td>
                            <td className="px-2 py-2 max-w-[120px] truncate" title={item.payload.thumbnail}>{item.payload.thumbnail}</td>
                            <td className="px-2 py-2 max-w-[120px] truncate" title={item.payload.videoPreview}>{item.payload.videoPreview}</td>
                            <td className="px-2 py-2">{(item.payload.tags || []).join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {importPreview.toCreate.length > 50 && (
                      <div className="text-xs text-gray-500 px-3 py-2 bg-gray-50 border-t">
                        ... and {importPreview.toCreate.length - 50} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              {importPreview.skipped.length > 0 && (
                <div className="mb-2">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Skipped Rows</h4>
                  <div className="border border-amber-200 rounded overflow-hidden bg-amber-50">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-100">
                        <tr>
                          <th className="text-left px-3 py-2">Row #</th>
                          <th className="text-left px-3 py-2">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.skipped.slice(0, 20).map((item) => (
                          <tr key={`skip-${item.row}`} className="border-t border-amber-200">
                            <td className="px-3 py-2">{item.row}</td>
                            <td className="px-3 py-2 text-amber-800">{item.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 p-5 border-t bg-gray-50 rounded-b-lg">
              <Button
                variant="outline"
                onClick={handleCancelImport}
                disabled={importing}
                data-testid="import-preview-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmImport}
                disabled={importing || (importPreview.toCreate.length === 0 && importPreview.toUpdate.length === 0)}
                className="flex items-center gap-2"
                data-testid="import-preview-confirm-btn"
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>Confirm Import</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-6">
        <div className="flex gap-3 items-center justify-between md:justify-start">
          <h2 className="text-xl md:text-2xl font-bold">Video Management</h2>
          {selectedVideos.length > 0 && (
            <Button variant="destructive" onClick={handleBulkDelete} size="sm">
              Delete {selectedVideos.length} Selected
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-wrap gap-2 md:items-center md:justify-end">
          {/* Hidden file input for import */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx"
            onChange={handleImportFile}
            style={{ display: 'none' }}
            data-testid="videos-import-file-input"
          />

          {/* Download Template */}
          <a
            href="https://templates.leolulu.shop/Upload%20Template.xlsx"
            target="_blank"
            rel="noopener noreferrer"
            download
            data-testid="videos-download-template-link"
            className="contents"
          >
            <Button variant="ghost" size="sm" className="w-full md:w-auto flex items-center justify-center gap-2">
              <FileDown className="w-4 h-4" />
              Template
            </Button>
          </a>

          {/* Import */}
          <Button
            variant="outline"
            onClick={handleImportClick}
            disabled={importing}
            className="w-full md:w-auto flex items-center justify-center gap-2"
            data-testid="videos-import-btn"
          >
            {importing ? (
              <div className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <UploadIcon className="w-4 h-4" />
            )}
            {importing ? 'Importing...' : 'Import'}
          </Button>

          {/* Undo Last Import */}
          {lastImport && (lastImport.created?.length > 0 || lastImport.updated?.length > 0) && (
            <Button
              variant="outline"
              onClick={handleUndoImport}
              disabled={undoing}
              className="w-full md:w-auto flex items-center justify-center gap-2 border-amber-400 text-amber-700 hover:bg-amber-50"
              title={`Revert ${lastImport.stats?.created || 0} created and ${lastImport.stats?.updated || 0} updated`}
              data-testid="videos-undo-import-btn"
            >
              {undoing ? (
                <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Undo2 className="w-4 h-4" />
              )}
              <span className="truncate">{undoing ? 'Undoing...' : 'Undo Import'}</span>
            </Button>
          )}

          {/* Export dropdown */}
          <div className="relative" ref={exportMenuRef}>
            <Button
              variant="outline"
              onClick={() => setShowExportMenu(v => !v)}
              className="w-full md:w-auto flex items-center justify-center gap-2"
              data-testid="videos-export-btn"
            >
              <Download className="w-4 h-4" />
              Export
            </Button>
            {showExportMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-20 min-w-[180px]">
                <button
                  onClick={handleExportCSV}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                  data-testid="videos-export-csv-btn"
                >
                  Export as CSV
                </button>
                <button
                  onClick={handleExportXLSX}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                  data-testid="videos-export-xlsx-btn"
                >
                  Export as XLSX
                </button>
              </div>
            )}
          </div>

          <Button
            onClick={() => {
              // Clear form data when opening Add form
              setVideoFormData({
                title: '',
                description: '',
                thumbnail: '',
                videoPreview: '',
                duration: '',
                videoCode: '',
                tags: ''
              });
              setShowAddVideoForm(!showAddVideoForm);
            }}
            className="col-span-2 sm:col-span-1 w-full md:w-auto flex items-center justify-center gap-2"
            data-testid="videos-add-new-btn"
          >
            <Plus className="w-4 h-4" />
            Add New Video
          </Button>
        </div>
      </div>

      {/* Add Video Form */}
      {showAddVideoForm && (
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold mb-4">Add New Video</h3>
          <div className="grid grid-cols-1 gap-4">
            <Input 
              name="title" 
              placeholder="Title *" 
              value={videoFormData.title} 
              onChange={handleVideoInputChange} 
            />
            
            <Input 
              name="videoCode" 
              placeholder="Video Code (e.g., #ABC123) *" 
              value={videoFormData.videoCode} 
              onChange={handleVideoInputChange} 
            />
            
            <Input 
              name="duration" 
              placeholder="Duration (e.g., 25:12) *" 
              value={videoFormData.duration} 
              onChange={handleVideoInputChange} 
            />
            
            <Textarea 
              name="description" 
              placeholder="Description *" 
              value={videoFormData.description} 
              onChange={handleVideoInputChange} 
              rows={3} 
            />
            
            <Input 
              name="tags" 
              placeholder="Tags (comma separated) *" 
              value={videoFormData.tags} 
              onChange={handleVideoInputChange} 
            />
            
            <R2Upload
              label="Thumbnail *"
              name="thumbnail"
              value={videoFormData.thumbnail}
              onChange={handleVideoInputChange}
              acceptedFormats="images"
              folderPath="thumbnails"
              placeholder="Enter URL or click Upload"
            />
            
            <R2Upload
              label="Video Preview"
              name="videoPreview"
              value={videoFormData.videoPreview}
              onChange={handleVideoInputChange}
              acceptedFormats="videos"
              folderPath="video-previews"
              placeholder="Enter URL or click Upload (optional)"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <Button onClick={handleAddVideo} className="w-full sm:w-auto">Add Video</Button>
            <Button variant="outline" onClick={() => setShowAddVideoForm(false)} className="w-full sm:w-auto">Cancel</Button>
          </div>
        </div>
      )}

      {/* Videos Table - desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
        <table className="w-full table-fixed">
          <colgroup>
            <col style={{ width: '40px' }} />
            <col style={{ width: '90px' }} />
            <col />
            <col style={{ width: '110px' }} />
            <col style={{ width: '90px' }} />
            <col style={{ width: '180px' }} />
            <col style={{ width: '70px' }} />
            <col style={{ width: '80px' }} />
            <col style={{ width: '110px' }} />
            <col style={{ width: '90px' }} />
          </colgroup>
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedVideos.length === sortedVideos.length && sortedVideos.length > 0}
                  onChange={handleSelectAll}
                  className="cursor-pointer"
                />
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">ID</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Title</th>
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-200 select-none"
                onClick={() => handleSort('videoCode')}
              >
                Code <SortIcon field="videoCode" />
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Duration</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Tags</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Thumb</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Preview</th>
              <th
                className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-200 select-none"
                onClick={() => handleSort('createdAt')}
              >
                Date <SortIcon field="createdAt" />
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedVideos.map((video) => (
              <tr key={video.id}>
                {editingId === video.id ? (
                  <td className="px-4 py-4" colSpan="10">
                    <div className="grid grid-cols-1 gap-3">
                      <Input name="title" placeholder="Title" value={videoFormData.title} onChange={handleVideoInputChange} />
                      <Input name="videoCode" placeholder="Code" value={videoFormData.videoCode} onChange={handleVideoInputChange} />
                      <Input name="duration" placeholder="Duration" value={videoFormData.duration} onChange={handleVideoInputChange} />
                      <Textarea name="description" placeholder="Description" value={videoFormData.description} onChange={handleVideoInputChange} rows={2} />
                      <Input name="tags" placeholder="Tags" value={videoFormData.tags} onChange={handleVideoInputChange} />

                      <R2Upload
                        label="Thumbnail"
                        name="thumbnail"
                        value={videoFormData.thumbnail}
                        onChange={handleVideoInputChange}
                        acceptedFormats="images"
                        folderPath="thumbnails"
                        placeholder="Enter URL or click Upload"
                      />

                      <R2Upload
                        label="Video Preview"
                        name="videoPreview"
                        value={videoFormData.videoPreview}
                        onChange={handleVideoInputChange}
                        acceptedFormats="videos"
                        folderPath="video-previews"
                        placeholder="Enter URL or click Upload"
                      />

                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleUpdateVideo(video.id)}><Save className="w-4 h-4 mr-1" />Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)}><X className="w-4 h-4 mr-1" />Cancel</Button>
                      </div>
                    </div>
                  </td>
                ) : (
                  <>
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedVideos.includes(video.id)}
                        onChange={() => handleSelectVideo(video.id)}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500 font-mono truncate">
                      <div className="flex items-center gap-1">
                        <span className="truncate">{video.id.slice(0, 6)}...</span>
                        <button
                          onClick={() => handleCopyId(video.id)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors shrink-0"
                          title="Copy full ID"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-sm truncate" title={video.title}>
                      {video.title}
                    </td>
                    <td className="px-3 py-3 text-sm font-mono truncate" title={video.videoCode}>
                      {video.videoCode}
                    </td>
                    <td className="px-3 py-3 text-sm whitespace-nowrap">
                      {video.duration}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {video.tags && video.tags.map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-sky-100 text-sky-700 text-xs rounded truncate max-w-[80px]" title={tag}>{tag}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      {video.thumbnail ? (
                        <span
                          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100 text-green-600"
                          title="Thumbnail uploaded"
                        >
                          <Check className="w-4 h-4" />
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-400"
                          title="No thumbnail"
                        >
                          <Minus className="w-4 h-4" />
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {video.videoPreview ? (
                        <span
                          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100 text-green-600"
                          title="Preview uploaded"
                        >
                          <Check className="w-4 h-4" />
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-400"
                          title="No preview"
                        >
                          <Minus className="w-4 h-4" />
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {video.createdAt ? new Date(video.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => handleEditVideo(video)} title="Edit" aria-label="Edit video">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="destructive" onClick={() => handleDeleteVideo(video.id)} title="Delete" aria-label="Delete video">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Videos list - mobile */}
      <div className="md:hidden space-y-3">
        {sortedVideos.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500 text-sm">
            No videos yet.
          </div>
        )}
        {sortedVideos.map((video) => (
          <div key={video.id} className="bg-white rounded-lg shadow-md overflow-hidden">
            {editingId === video.id ? (
              <div className="p-4 space-y-3">
                <Input name="title" placeholder="Title" value={videoFormData.title} onChange={handleVideoInputChange} />
                <Input name="videoCode" placeholder="Code" value={videoFormData.videoCode} onChange={handleVideoInputChange} />
                <Input name="duration" placeholder="Duration" value={videoFormData.duration} onChange={handleVideoInputChange} />
                <Textarea name="description" placeholder="Description" value={videoFormData.description} onChange={handleVideoInputChange} rows={3} />
                <Input name="tags" placeholder="Tags" value={videoFormData.tags} onChange={handleVideoInputChange} />
                <R2Upload
                  label="Thumbnail"
                  name="thumbnail"
                  value={videoFormData.thumbnail}
                  onChange={handleVideoInputChange}
                  acceptedFormats="images"
                  folderPath="thumbnails"
                  placeholder="Enter URL or click Upload"
                />
                <R2Upload
                  label="Video Preview"
                  name="videoPreview"
                  value={videoFormData.videoPreview}
                  onChange={handleVideoInputChange}
                  acceptedFormats="videos"
                  folderPath="video-previews"
                  placeholder="Enter URL or click Upload"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => handleUpdateVideo(video.id)} className="flex-1">
                    <Save className="w-4 h-4 mr-1" />Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="flex-1">
                    <X className="w-4 h-4 mr-1" />Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {/* Header row: checkbox, title, actions */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedVideos.includes(video.id)}
                    onChange={() => handleSelectVideo(video.id)}
                    className="cursor-pointer mt-1 shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{video.title}</div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                      <span className="font-mono truncate">{video.videoCode}</span>
                      <span>·</span>
                      <span>{video.duration}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => handleEditVideo(video)} title="Edit" aria-label="Edit video">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="destructive" onClick={() => handleDeleteVideo(video.id)} title="Delete" aria-label="Delete video">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Tags */}
                {video.tags && video.tags.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {video.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 bg-sky-100 text-sky-700 text-xs rounded">{tag}</span>
                    ))}
                  </div>
                )}

                {/* Status row: thumb, preview, date, id */}
                <div className="flex items-center gap-3 text-xs text-gray-600 pt-1 border-t border-gray-100">
                  <div className="flex items-center gap-1.5" title="Thumbnail">
                    {video.thumbnail ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Minus className="w-4 h-4 text-gray-400" />
                    )}
                    <span>Thumb</span>
                  </div>
                  <div className="flex items-center gap-1.5" title="Preview">
                    {video.videoPreview ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Minus className="w-4 h-4 text-gray-400" />
                    )}
                    <span>Preview</span>
                  </div>
                  <span className="ml-auto text-gray-500">
                    {video.createdAt ? new Date(video.createdAt).toLocaleDateString() : ''}
                  </span>
                </div>

                {/* ID row (small, with copy) */}
                <div className="flex items-center gap-1 text-[11px] text-gray-400 font-mono">
                  <span className="truncate">{video.id}</span>
                  <button
                    onClick={() => handleCopyId(video.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Copy ID"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 text-gray-600">Total Videos: {videos.length}</div>
    </div>
  );
};

export default VideosTable;
